import React from 'react'
import ls from '@livesession/browser'
import { fromJS } from 'immutable'
import ModalTryInterview from './ModalTryInterview.jsx'

import { accesslog } from '../../helpers/global.js'
import { publicRecommendsGenerateInterviewApi, publicRecommendsSubmitInterviewAnswerApi } from '../../apis/recommends.js'

const JOB_TITLE_MAX_LENGTH = 150
const ASSESS_SKILL_MAX_LENGTH = 150

// Non-real sample question, shown when ?sample is set or as a dev/QA fallback.
const DUMMY_ASSESS_QUESTIONS = [
  { id: 'assess-q-communication', skillMatch: 'communication', prompt: 'Describe a time you had to explain a complex idea to someone with no background in the topic. How did you make sure they understood?' },
  { id: 'assess-q-problem-solving', skillMatch: 'problem solving', prompt: 'Tell me about a situation where the obvious solution to a problem turned out to be wrong. What did you do next?' },
  { id: 'assess-q-leadership', skillMatch: 'leadership', prompt: 'Describe a time you had to motivate a team member who was disengaged. What approach did you take?' },
  { id: 'assess-q-stakeholder', skillMatch: 'stakeholder', prompt: 'Tell us about a time you had to align two stakeholders with conflicting priorities.' },
  { id: 'assess-q-generic', skillMatch: null, prompt: 'Describe a recent challenge in your work related to {skill} and how you approached it.' }
]

// Loosely matches the entered skill against DUMMY_ASSESS_QUESTIONS' keywords,
// falling back to the generic templated prompt for anything unmatched.
function getRandomDummyQuestion(skill) {
  const normalized = String(skill).toLowerCase()
  const match = DUMMY_ASSESS_QUESTIONS.find((question) => question.skillMatch && normalized.includes(question.skillMatch))
  const chosen = match || DUMMY_ASSESS_QUESTIONS[DUMMY_ASSESS_QUESTIONS.length - 1]
  return { ...chosen, prompt: chosen.prompt.replace('{skill}', skill || 'this skill') }
}

// Non-real sample question_code for the ?sample dev/QA path, standing in for the
// real log-correlation code the interview-question endpoint returns.
function getRandomDummyQuestionCode() {
  return `sample-${Math.random().toString(36).slice(2, 10)}`
}

// InterviewContainer expects an Immutable.js Map with a video_question shape --
// wrap the plain generated question object once here rather than inline in
// render, so unrelated setState calls elsewhere on this page don't produce a
// new object reference and re-trigger InterviewContainer's camera effect.
function buildInterviewQuestionRecord(question) {
  return fromJS({
    id: question.id,
    title: question.prompt,
    prompt: question.prompt,
    type_parent: 'video_question',
    user_answer: null
  })
}

// Non-real sample result, shown only when ?sample is set. Shape matches the
// real /interview-answer response's `result` field ({ rubric: [{ criterion,
// tier, description }], justification }) so rendering doesn't need to branch
// between mock and real data.
const DUMMY_ASSESS_RESULT_TEMPLATE = {
  rubric: [
    { criterion: 'Clarity of communication', tier: 'Strong', description: 'Your answer was easy to follow and logically ordered.' },
    { criterion: 'Depth of example', tier: 'Adequate', description: 'A concrete example was given, though more detail on the outcome would strengthen it.' },
    { criterion: 'Relevance to the skill', tier: 'Adequate', description: 'Mostly on-topic, with room to tie the example more directly back to {skill}.' },
    { criterion: 'Structure (situation, action, result)', tier: 'Weak', description: 'The result or impact of your action wasn\'t clearly stated.' }
  ],
  justification: 'Overall this is a promising first answer. Adding a clearer outcome and connecting your example more explicitly back to {skill} would push this from Adequate to Strong across the board.'
}

function getDummyAssessResult(skill) {
  const skillLabel = skill || 'this skill'
  return {
    rubric: DUMMY_ASSESS_RESULT_TEMPLATE.rubric.map((row) => ({ ...row, description: row.description.replace('{skill}', skillLabel) })),
    justification: DUMMY_ASSESS_RESULT_TEMPLATE.justification.replace('{skill}', skillLabel)
  }
}

// Non-real sample transcript for the ?sample dev/QA path, standing in for the real
// transcript interview-answer returns alongside `result`.
const DUMMY_ASSESS_ANSWER_TEXT = 'This is a sample recorded answer, shown only for the ?sample dev/QA preview.'

// Self-contained "generate a question -> answer on video" widget, backing both the
// dedicated /talent-acquisition/try-questions page and the homepage's "Try It Yourself"
// section (in `compact` mode there). Calls back via
// `onAnswered(result, { skill, jobTitle, questionText, questionCode, answerText })`
// once a real or sample scoring result is ready -- the scoring preview itself (and its
// sign-up gate) is rendered separately by TryQuestionsResult, since the two pages lay the
// question flow and the result preview out differently. `source` tags accesslog/livesession
// events with where the widget was opened from.
class TryQuestionsWidget extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      assessSkill: '',
      assessJobTitle: '',
      assessStep: 'input', // 'input' | 'generating' | 'question' | 'answered' | 'error'
      generatedQuestion: null, // { id, prompt } from the API/mock
      questionCode: null, // log-correlation id, generate -> submit (interview-question's question_code)
      answerConstraints: null, // { min_duration_sec, max_duration_sec } from the API, or null pre-question/sample
      interviewQuestionRecord: null, // Immutable Map built once, fed to ModalTryInterview
      isGeneratingQuestion: false,
      isInterviewModalOpen: false,
      assessError: ''
    }
  }

  // Dev/QA shortcut: ?sample returns mock results instead of calling the API,
  // since the interview-question/interview-answer APIs aren't reachable from local.
  useSampleData = () => {
    return new URLSearchParams(window.location.search).has('sample')
  }

  handleBeforeUnload = (e) => {
    e.preventDefault()
    e.returnValue = ''
  }

  componentDidUpdate(prevProps, prevState) {
    const wasGenerating = prevState.assessStep === 'generating'
    const isGenerating = this.state.assessStep === 'generating'
    if (isGenerating && !wasGenerating) {
      window.addEventListener('beforeunload', this.handleBeforeUnload)
    } else if (!isGenerating && wasGenerating) {
      window.removeEventListener('beforeunload', this.handleBeforeUnload)
    }
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  }

  trackFieldFilled = (eventName, value, extra) => {
    if (String(value).trim()) {
      ls.track(eventName, extra)
    }
  }

  handleGenerateQuestion = () => {
    const { source } = this.props
    const { assessSkill, assessJobTitle } = this.state
    if (!assessSkill.trim()) {
      return
    }

    accesslog(source, 'button_generate_question')
    this.setState({ assessStep: 'generating', isGeneratingQuestion: true, assessError: '' })

    if (this.useSampleData()) {
      const question = getRandomDummyQuestion(assessSkill)
      accesslog(source, 'button_start_interview')
      this.setState({
        assessStep: 'question',
        isGeneratingQuestion: false,
        generatedQuestion: question,
        questionCode: getRandomDummyQuestionCode(),
        answerConstraints: { min_duration_sec: 5, max_duration_sec: 120 },
        interviewQuestionRecord: buildInterviewQuestionRecord(question),
        isInterviewModalOpen: true
      })
      return
    }

    publicRecommendsGenerateInterviewApi({ skill: assessSkill.trim(), job_title: assessJobTitle.trim() })
      .then((response) => {
        const { question, question_code: questionCode, answer_constraints: answerConstraints } = response.data
        if (!question || !questionCode) {
          this.setState({ assessStep: 'error', isGeneratingQuestion: false, assessError: 'Something went wrong generating your question. Please try again.' })
          return
        }
        accesslog(source, 'button_start_interview')
        this.setState({
          assessStep: 'question',
          isGeneratingQuestion: false,
          generatedQuestion: question,
          questionCode,
          answerConstraints: answerConstraints || null,
          interviewQuestionRecord: buildInterviewQuestionRecord(question),
          isInterviewModalOpen: true
        })
      })
      .catch((error) => {
        const status = error.response && error.response.status
        let message = 'Something went wrong generating your question. Please try again.'
        if (status === 429) {
          const retryAfter = error.response.headers && error.response.headers['retry-after']
          message = retryAfter
            ? `Too many requests — please try again in ${retryAfter} seconds.`
            : 'Too many requests. Please wait a moment and try again.'
        } else if (status === 422 || status === 400) {
          message = (error.response.data && error.response.data.error) || message
        }
        this.setState({ assessStep: 'error', isGeneratingQuestion: false, assessError: message })
      })
  }

  openInterviewModal = () => {
    accesslog(this.props.source, 'button_start_interview')
    this.setState({ isInterviewModalOpen: true })
  }

  closeInterviewModal = () => {
    this.setState({ isInterviewModalOpen: false })
  }

  handleSubmitInterviewAnswer = (blob) => {
    const { source, onAnswered } = this.props
    const { assessSkill, assessJobTitle, generatedQuestion, questionCode } = this.state

    accesslog(source, 'button_submit_assess_answer')

    if (this.useSampleData()) {
      this.setState({ isInterviewModalOpen: false, assessStep: 'answered' })
      onAnswered(getDummyAssessResult(assessSkill), {
        skill: assessSkill,
        jobTitle: assessJobTitle,
        questionText: generatedQuestion && generatedQuestion.prompt,
        questionCode,
        answerText: DUMMY_ASSESS_ANSWER_TEXT
      })
      return Promise.resolve()
    }

    const formData = new FormData()
    formData.append('video', blob, blob.name || 'try_recorded_video.mp4')
    formData.append('skill', assessSkill.trim())
    formData.append('job_title', assessJobTitle.trim())
    formData.append('question_text', (generatedQuestion && generatedQuestion.prompt) || '')
    formData.append('question_code', questionCode || '')

    // Stateless -- transcribes and scores the answer in this same request, no signup
    // or session step. `result` is the real, complete rubric/justification.
    return publicRecommendsSubmitInterviewAnswerApi(formData)
      .then((response) => {
        const { result, transcript } = response.data
        this.setState({ isInterviewModalOpen: false, assessStep: 'answered' })
        onAnswered(result, {
          skill: assessSkill,
          jobTitle: assessJobTitle,
          questionText: generatedQuestion && generatedQuestion.prompt,
          questionCode,
          answerText: transcript
        })
      })
      .catch((error) => {
        const apiMessage = error.response && error.response.data && error.response.data.error
        throw new Error(apiMessage || 'Something went wrong submitting your answer. Please try again.')
      })
  }

  renderAssessForm() {
    const { compact } = this.props
    const { assessSkill, assessJobTitle, assessStep, isGeneratingQuestion, generatedQuestion, assessError, answerConstraints } = this.state

    if (assessStep === 'input') {
      return (
        <div className="recommends-assess-form">
          <label htmlFor="assess-skill">What skill do you want to test? <span className="required">*</span></label>
          <input
            id="assess-skill"
            type="text"
            placeholder="e.g. leadership, stakeholder management, problem solving"
            maxLength={ASSESS_SKILL_MAX_LENGTH}
            value={assessSkill}
            onChange={(e) => this.setState({ assessSkill: e.target.value })}
            onBlur={() => this.trackFieldFilled('assess_skill_filled', assessSkill)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                this.handleGenerateQuestion()
              }
            }}
            data-ls-id="assess-skill-input"
          />
          {!compact && (
            <p className="recommends-field-helper">Choose one skill or focus area. This is what your one question will be based on.</p>
          )}

          <label htmlFor="assess-job-title">Job title</label>
          <input
            id="assess-job-title"
            type="text"
            placeholder="e.g. Senior Engineer (optional)"
            maxLength={JOB_TITLE_MAX_LENGTH}
            value={assessJobTitle}
            onChange={(e) => this.setState({ assessJobTitle: e.target.value })}
            onBlur={() => this.trackFieldFilled('assess_job_title_filled', assessJobTitle)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                this.handleGenerateQuestion()
              }
            }}
            data-ls-id="assess-job-title-input"
          />

          <button
            className="search-button recommends-search-button recommends-score-submit"
            type="button"
            disabled={isGeneratingQuestion || !assessSkill.trim()}
            onClick={this.handleGenerateQuestion}
            data-ls-id="assess-generate-question-button"
          >
            {isGeneratingQuestion && <i className="fa fa-spin fa-spinner recommends-spinner-icon" />}
            {isGeneratingQuestion ? 'Generating question...' : 'Give Me a Question'}
          </button>
        </div>
      )
    }

    if (assessStep === 'generating') {
      return (
        <div className="recommends-assess-form recommends-question-card">
          <p className="recommends-question-prompt recommends-loading-status">
            <i className="fa fa-spin fa-spinner recommends-spinner-icon" />
            Generating your question...
          </p>
          <div className="recommends-skeleton-line" />
          <div className="recommends-skeleton-line recommends-skeleton-line-short" />
        </div>
      )
    }

    if (assessStep === 'error') {
      return (
        <div className="recommends-assess-form recommends-question-card">
          <p className="recommends-field-error">{assessError}</p>
          <button
            className="search-button recommends-search-button recommends-score-submit"
            type="button"
            onClick={this.handleGenerateQuestion}
            data-ls-id="assess-retry-question-button"
          >
            Try Again
          </button>
        </div>
      )
    }

    return (
      <div className="recommends-assess-form recommends-question-card">
        <h4 className="recommends-question-prompt">{generatedQuestion && generatedQuestion.prompt}</h4>

        {assessStep === 'question' && (
          <React.Fragment>
            <p className="recommends-field-helper">
              You'll answer this with a short video{answerConstraints ? ` (${answerConstraints.min_duration_sec}–${answerConstraints.max_duration_sec} seconds)` : ''}. Make sure your camera and microphone are ready.
            </p>
            <button
              className="search-button recommends-search-button recommends-score-submit"
              type="button"
              onClick={this.openInterviewModal}
              data-ls-id="assess-start-interview-button"
            >
              Start Interview
            </button>
          </React.Fragment>
        )}

        {assessStep === 'answered' && (
          <p className="recommends-field-helper">Your video answer has been submitted.</p>
        )}
      </div>
    )
  }

  render() {
    const { compact } = this.props

    return (
      <div className={`recommends-widget ${compact ? 'recommends-widget-compact' : ''}`}>
        {this.renderAssessForm()}

        <ModalTryInterview
          isOpen={this.state.isInterviewModalOpen}
          onRequestClose={this.closeInterviewModal}
          question={this.state.interviewQuestionRecord}
          onSubmitAnswer={this.handleSubmitInterviewAnswer}
          maxDurationSec={this.state.answerConstraints && this.state.answerConstraints.max_duration_sec}
        />
      </div>
    )
  }
}

TryQuestionsWidget.defaultProps = {
  source: 'try-questions',
  compact: false
}

export default TryQuestionsWidget
